import type { FC } from 'react';
import React, { useContext } from 'react';
import { flex } from 'src/styling/constants';
import { WebsiteLink } from 'src/components/common/website-link';
import { amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import capitalize from 'lodash/capitalize';
import { isClubLinkCurrentPage, isGlobalLinkCurrentPage } from 'src/helpers/navigation-helper';
import LanguageSelection from 'src/components/layout/redesign/header-shared/language-selection';
import HeaderDesktopMenu from 'src/components/layout/redesign/header-desktop/header-menu';
import { HeaderActions } from 'src/components/layout/redesign/header-desktop/header-action';
import { mqDesktopHeader } from 'src/components/layout/redesign/header-shared/header-styling';
import type { Styling } from 'src/context/styling';
import type { CSSInterpolation } from '@emotion/serialize';
import { SiteContext } from 'src/context/site-context';
import useWindowSize from 'src/custom-hooks/use-window-size';
import type { BaseHeaderContent } from 'src/components/layout/redesign/header-mobile/header-menu';
import type { HeaderButtonProps } from 'src/components/layout/redesign/header-mobile/header-button';
import { getClubInformation } from 'src/content/club-look-ups-by-id';
import { HeaderLogo } from 'src/components/layout/redesign/header-shared/header-logo';

const CLUB_NAME_LENGTH_TO_REDUCE_SIZE = 15;

const createComponentStyling = (styling: Styling, isGlobalPage: boolean) =>
  ({
    logo: [
      mqDesktopHeader({
        display: isGlobalPage ? ['initial', 'initial', 'none'] : 'flex',
        alignItems: 'center',
        alignSelf: 'center',
        marginRight: '1.5rem',
        gap: '2rem',
      }),
    ],
    navigationContainer: mqDesktopHeader({
      gap: '0.75rem',
      flexGrow: 1,
    }),
    primaryNavContainer: mqDesktopHeader({
      padding: isGlobalPage ? ['0 1rem 0 0', '0 1rem 0 0', '0 1rem'] : '0 1rem 0 0',
      marginLeft: isGlobalPage ? ['-0.75rem', '-0.75rem', '0'] : '-0.75rem',
      flexBasis: 'max-content',
      flexGrow: 0,
      alignItems: 'center',
    }),
    primaryNavLink: {
      margin: '0.75rem',
      fontSize: '1.1875rem',
      lineHeight: '145%',
      textAlign: 'center',
      borderBottom: '1px solid',
      borderBottomColor: 'transparent',
    },
    secondaryNavContainer: {
      gap: '0.5rem',
      alignSelf: 'center',
      marginLeft: 'auto',
    },
    secondaryNavLink: [
      styling.fonts.regular,
      {
        margin: '0.5rem',
        fontSize: '0.875rem',
        lineHeight: '160%',
        borderBottom: '1px solid',
        borderBottomColor: 'transparent',
      },
    ],
    hover: {
      '&:hover': {
        color: styling.colors.camelot,
        borderBottomColor: styling.colors.camelot,
      },
    },
    selected: {
      color: styling.colors.camelot,
      borderBottomColor: styling.colors.camelot,
    },
    active: {
      borderBottomColor: styling.colors.capeCod,
    },
    inlineActionsWrapper: mqDesktopHeader({
      display: isGlobalPage ? ['none', 'flex'] : 'flex',
      flexGrow: 1,
      justifyContent: 'flex-end',
      flexBasis: 'min-content',
    }),
  }) satisfies Record<string, CSSInterpolation>;

export type HeaderNavigationProps = {
  logoClick: () => void;
  isUserTabbing: boolean;
  headerContent: BaseHeaderContent;
  primaryButton?: HeaderButtonProps;
  secondaryButton?: HeaderButtonProps;
  openSubMenu: (index: number) => void;
  setIsUserTabbing: (val: boolean) => void;
  displayedSubMenuIndex: number | null;
  canOpenSubMenu: boolean;
  setCanOpenSubMenu: (val: boolean) => void;
  onHover: () => void;
  onFinishMenuAction: () => void;
};

const HeaderNavigation: FC<HeaderNavigationProps> = ({
  logoClick,
  isUserTabbing,
  headerContent,
  primaryButton,
  secondaryButton,
  openSubMenu,
  setIsUserTabbing,
  displayedSubMenuIndex,
  canOpenSubMenu,
  setCanOpenSubMenu,
  onHover,
  onFinishMenuAction,
}) => {
  const { styling, language, buildType, basePath, pageLevel, isGlobalPage, siteId } =
    useContext(SiteContext);
  const { isMobileHeader } = useWindowSize();
  const componentStyling = createComponentStyling(styling, isGlobalPage);

  const onPrimaryNavHover = (event: React.PointerEvent<HTMLLIElement>, index: number) => {
    if (!canOpenSubMenu) {
      return;
    }

    openSubMenu(index);
    event.stopPropagation();
  };

  const isLinkCurrentPage = (link: string) =>
    isGlobalPage
      ? isGlobalLinkCurrentPage(link, language, buildType, basePath)
      : isClubLinkCurrentPage(link, language, siteId, basePath);

  const onPrimaryNavClick = (link: string) => {
    setIsUserTabbing(false);

    if (!isLinkCurrentPage(link)) {
      setCanOpenSubMenu(false);
      onFinishMenuAction();
    }
  };

  return (
    <div css={[componentStyling.navigationContainer, flex.column]}>
      <div css={flex.row}>
        <div css={[componentStyling.logo]} onPointerOver={onHover}>
          <WebsiteLink to="/" goToGlobal handleClick={logoClick}>
            <HeaderLogo davidLloydSize="9.375rem" meridianSize="9.5rem" />
          </WebsiteLink>
          {!isGlobalPage && (
            <p
              css={[
                styling.fonts.regular,
                mqDesktopHeader({
                  fontSize: [
                    getClubInformation(siteId).clubName.length > CLUB_NAME_LENGTH_TO_REDUCE_SIZE
                      ? '2rem'
                      : '2.5rem',
                    '2.5rem',
                  ],
                  lineHeight: '145%',
                  margin: 0,
                  textWrap: 'nowrap',
                }),
              ]}
            >
              {getClubInformation(siteId).clubName}
            </p>
          )}
        </div>
        <nav css={componentStyling.secondaryNavContainer}>
          <ul css={[flex.row, { alignItems: 'center', textAlign: 'center' }]}>
            {headerContent?.headerSecondaryUpperMenu?.map(
              ({ text, link, openInNewTab, goToGlobal }) => {
                const handleClick = () => {
                  onFinishMenuAction();
                  return amplitudeTrackEvent(`Click to ${capitalize(text || '')} - Navigation`, {
                    custom_page: window.location.origin,
                    custom_level: pageLevel,
                  });
                };

                return (
                  <li key={text}>
                    <WebsiteLink
                      to={link}
                      openInNewTab={!!openInNewTab}
                      handleClick={handleClick}
                      goToGlobal={goToGlobal ?? undefined}
                    >
                      <div
                        css={[
                          componentStyling.secondaryNavLink,
                          isLinkCurrentPage(link) ? componentStyling.active : null,
                          componentStyling.hover,
                        ]}
                        onPointerOver={onHover}
                      >
                        {text}
                      </div>
                    </WebsiteLink>
                  </li>
                );
              }
            )}
            <LanguageSelection text={headerContent?.headerOtherText?.selectLanguageText} />
          </ul>
        </nav>
      </div>
      <div css={flex.row}>
        <nav
          css={[componentStyling.primaryNavContainer, flex.rowCentered]}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget) && isUserTabbing) {
              onFinishMenuAction();
            }
          }}
        >
          <ul css={flex.row}>
            {headerContent?.headerMenuItems?.map((item, index) => {
              const handleClick = () => {
                onFinishMenuAction();
                return amplitudeTrackEvent(`Click to ${capitalize(item.name || '')} - Navigation`, {
                  custom_page: window.location.origin,
                  custom_level: pageLevel,
                });
              };
              return (
                <li key={item.name} onPointerOver={(event) => onPrimaryNavHover(event, index)}>
                  {item.link ? (
                    <WebsiteLink
                      to={item.link}
                      css={flex.rowCentered}
                      onFocus={() => {
                        openSubMenu(index);
                        setIsUserTabbing(true);
                      }}
                      handleClick={handleClick}
                    >
                      <div
                        data-testid={item.name}
                        css={[
                          componentStyling.primaryNavLink,
                          styling.fonts.regular,
                          isLinkCurrentPage(item.link) ? componentStyling.active : null,
                          displayedSubMenuIndex === index ? componentStyling.selected : null,
                        ]}
                        onClick={() => onPrimaryNavClick(item.link!)}
                      >
                        {item.name}
                      </div>
                    </WebsiteLink>
                  ) : (
                    <div
                      data-testid={item.name}
                      css={[
                        componentStyling.primaryNavLink,
                        styling.fonts.regular,
                        displayedSubMenuIndex === index ? componentStyling.selected : null,
                        { cursor: 'default' },
                      ]}
                      tabIndex={0}
                      onFocus={() => {
                        openSubMenu(index);
                        setIsUserTabbing(true);
                      }}
                      onClick={() => {
                        setIsUserTabbing(false);
                      }}
                    >
                      {item.name}
                    </div>
                  )}
                  {!isMobileHeader && (
                    <HeaderDesktopMenu
                      {...item}
                      overviewText={headerContent.headerOtherText?.overviewText ?? ''}
                      visible={displayedSubMenuIndex === index}
                      onFinish={onFinishMenuAction}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
        <div css={componentStyling.inlineActionsWrapper}>
          <HeaderActions
            primaryButton={primaryButton}
            secondaryButton={secondaryButton}
            onHover={onHover}
            linkDropdownCta={headerContent?.linkDropdownCta}
          />
        </div>
      </div>
    </div>
  );
};

export { HeaderNavigation };
